/**
 * Pure, React-vrije helpers voor de smart `LocatiePicker` (spec NN §3.2.1B).
 *
 * De `LocatiePicker` is één zoekbare combobox die put uit alle bestaande
 * locatie-bronnen in het systeem (eigen BV-vestigingen, klantlocaties,
 * opleider-locaties) plus de sentinels "Online" en "Nieuw adres via Google
 * Places". Deze module bevat alle logica die zónder React getest kan worden:
 * opties bouwen, een gekozen `ComboboxOption.value` terug-decoderen naar een
 * `LocatiePickerValue`, en heen-en-weer mappen tussen een waarde en haar
 * trigger-/option-label.
 *
 * `@streep/ui` heeft bewust géén dependency op `@streep/types`. Het value-type
 * hieronder is daarom lokaal gedefinieerd, maar structureel identiek aan
 * `@streep/types`' `KalenderEventLocatieDTO` (discriminated union op `type`),
 * zodat de backoffice de picker-waarde 1:1 kan mappen naar de API-DTO.
 */

import { filterOptions, type ComboboxOption } from './combobox-filter'

// ── Types (lokaal — spiegel van @streep/types KalenderEventLocatieDTO) ──────

export type LocatiePickerType =
  | 'ENTITY'
  | 'CLIENT_LOCATION'
  | 'OPLEIDER_LOCATIE'
  | 'ONLINE'
  | 'CUSTOM'

export type LocatiePickerValue =
  | { type: 'ENTITY'; entityId: string; label: string; lat: number | null; lng: number | null }
  | {
      type: 'CLIENT_LOCATION'
      clientLocationId: string
      label: string
      lat: number | null
      lng: number | null
    }
  | {
      type: 'OPLEIDER_LOCATIE'
      opleiderLocatieId: string
      label: string
      lat: number | null
      lng: number | null
    }
  | { type: 'ONLINE'; label?: string }
  | { type: 'CUSTOM'; adres: string; lat: number | null; lng: number | null; placeId?: string | null }

export interface LocatiePickerSources {
  entities: { id: string; naam: string; lat: number | null; lng: number | null }[]
  clientLocations: { id: string; label: string; lat: number | null; lng: number | null }[]
  opleiderLocaties: {
    id: string
    naam: string
    opleiderNaam: string
    lat: number | null
    lng: number | null
  }[]
}

export interface BuildLocatieOptionsOpts {
  allowOnline: boolean
  allowCustom: boolean
  /**
   * Indien gezet: toon UITSLUITEND opties van deze types. Bv.
   * `['OPLEIDER_LOCATIE']` → alleen opleider-locaties, geen Online/Custom/
   * Entity/ClientLocation. Gebruikt om TRAINING-events op de opleider-locatie
   * van de sessie te locken (spec §3.2.1B).
   */
  lockedTypes?: LocatiePickerType[]
}

// ── Value-encoding ──────────────────────────────────────────────────────────
// Elke ComboboxOption.value krijgt een type-prefix zodat we bij selectie weten
// uit welke bron de keuze kwam. Sentinels voor de twee niet-bron-opties.

const ENTITY_PREFIX = 'entity:'
const CLIENT_LOCATION_PREFIX = 'clientloc:'
const OPLEIDER_LOCATIE_PREFIX = 'opleiderloc:'
export const ONLINE_SENTINEL = '__online__'
export const CUSTOM_SENTINEL = '__custom__'

// ── Groepskoppen ──────────────────────────────────────────────────────────
const GROUP_ENTITIES = 'Eigen vestigingen'
const GROUP_CLIENT_LOCATIONS = 'Klantlocaties'
const GROUP_OPLEIDER_LOCATIES = 'Opleider-locaties'

const ONLINE_LABEL = 'Online (Teams/Zoom)'
const CUSTOM_OPTION_LABEL = '➕ Nieuw adres via Google Places…'

function typeAllowed(type: LocatiePickerType, lockedTypes?: LocatiePickerType[]): boolean {
  if (!lockedTypes || lockedTypes.length === 0) return true
  return lockedTypes.includes(type)
}

/**
 * Bouw de gegroepeerde combobox-opties uit alle locatie-bronnen.
 *
 * - Groepen: "Eigen vestigingen" / "Klantlocaties" / "Opleider-locaties".
 * - `hint`: plaats (entity/klant) of opleidernaam (opleider-locatie).
 * - `value`: type-geprefixte identifier (`entity:<id>`, `clientloc:<id>`,
 *   `opleiderloc:<id>`) of een sentinel (`__online__` / `__custom__`).
 * - Online verschijnt alleen als `allowOnline`; "Nieuw adres via Google
 *   Places…" alleen als `allowCustom`.
 * - `lockedTypes`: filtert álle opties (inclusief de sentinels) op de
 *   toegestane types.
 */
export function buildLocatieOptions(
  sources: LocatiePickerSources,
  opts: BuildLocatieOptionsOpts,
): ComboboxOption[] {
  const { allowOnline, allowCustom, lockedTypes } = opts
  const options: ComboboxOption[] = []

  if (typeAllowed('ENTITY', lockedTypes)) {
    for (const e of sources.entities) {
      options.push({
        value: `${ENTITY_PREFIX}${e.id}`,
        label: e.naam,
        group: GROUP_ENTITIES,
      })
    }
  }

  if (typeAllowed('CLIENT_LOCATION', lockedTypes)) {
    for (const c of sources.clientLocations) {
      options.push({
        value: `${CLIENT_LOCATION_PREFIX}${c.id}`,
        label: c.label,
        group: GROUP_CLIENT_LOCATIONS,
      })
    }
  }

  if (typeAllowed('OPLEIDER_LOCATIE', lockedTypes)) {
    for (const o of sources.opleiderLocaties) {
      options.push({
        value: `${OPLEIDER_LOCATIE_PREFIX}${o.id}`,
        label: o.naam,
        hint: o.opleiderNaam,
        group: GROUP_OPLEIDER_LOCATIES,
      })
    }
  }

  if (allowOnline && typeAllowed('ONLINE', lockedTypes)) {
    options.push({ value: ONLINE_SENTINEL, label: ONLINE_LABEL })
  }

  if (allowCustom && typeAllowed('CUSTOM', lockedTypes)) {
    options.push({ value: CUSTOM_SENTINEL, label: CUSTOM_OPTION_LABEL })
  }

  return options
}

/**
 * Decodeer een gekozen `ComboboxOption.value` terug naar een
 * `LocatiePickerValue`. Bron-opties (entity/klant/opleider) worden uit
 * `sources` opgezocht zodat label + lat/lng meekomen. De custom-sentinel geeft
 * `{ sentinel: 'CUSTOM' }` terug — dat betekent "schakel naar de Places-
 * invoermodus" (de uiteindelijke CUSTOM-waarde ontstaat pas na een Places-
 * selectie). Onbekende/niet-bestaande values → `null`.
 */
export function decodeLocatieOptionValue(
  encoded: string,
  sources: LocatiePickerSources,
): LocatiePickerValue | { sentinel: 'CUSTOM' } | null {
  if (encoded === ONLINE_SENTINEL) {
    return { type: 'ONLINE' }
  }
  if (encoded === CUSTOM_SENTINEL) {
    return { sentinel: 'CUSTOM' }
  }

  if (encoded.startsWith(ENTITY_PREFIX)) {
    const id = encoded.slice(ENTITY_PREFIX.length)
    const found = sources.entities.find((e) => e.id === id)
    if (!found) return null
    return { type: 'ENTITY', entityId: found.id, label: found.naam, lat: found.lat, lng: found.lng }
  }

  if (encoded.startsWith(CLIENT_LOCATION_PREFIX)) {
    const id = encoded.slice(CLIENT_LOCATION_PREFIX.length)
    const found = sources.clientLocations.find((c) => c.id === id)
    if (!found) return null
    return {
      type: 'CLIENT_LOCATION',
      clientLocationId: found.id,
      label: found.label,
      lat: found.lat,
      lng: found.lng,
    }
  }

  if (encoded.startsWith(OPLEIDER_LOCATIE_PREFIX)) {
    const id = encoded.slice(OPLEIDER_LOCATIE_PREFIX.length)
    const found = sources.opleiderLocaties.find((o) => o.id === id)
    if (!found) return null
    return {
      type: 'OPLEIDER_LOCATIE',
      opleiderLocatieId: found.id,
      label: found.naam,
      lat: found.lat,
      lng: found.lng,
    }
  }

  return null
}

/** Leesbare label voor de combobox-trigger (huidige selectie). */
export function locatieValueToTriggerLabel(value: LocatiePickerValue | null): string {
  if (!value) return ''
  switch (value.type) {
    case 'CUSTOM':
      return value.adres
    case 'ONLINE':
      return ONLINE_LABEL
    case 'ENTITY':
    case 'CLIENT_LOCATION':
    case 'OPLEIDER_LOCATIE':
      return value.label
  }
}

/**
 * Omgekeerde van {@link decodeLocatieOptionValue}: zet een huidige waarde om
 * naar de bijbehorende `ComboboxOption.value`, zodat de selectie in de lijst
 * gehighlight kan worden. CUSTOM heeft geen lijst-optie (eigen invoer-modus) →
 * `null`.
 */
export function locatieValueToOptionValue(value: LocatiePickerValue | null): string | null {
  if (!value) return null
  switch (value.type) {
    case 'ENTITY':
      return `${ENTITY_PREFIX}${value.entityId}`
    case 'CLIENT_LOCATION':
      return `${CLIENT_LOCATION_PREFIX}${value.clientLocationId}`
    case 'OPLEIDER_LOCATIE':
      return `${OPLEIDER_LOCATIE_PREFIX}${value.opleiderLocatieId}`
    case 'ONLINE':
      return ONLINE_SENTINEL
    case 'CUSTOM':
      return null
  }
}

/** Re-export zodat callers die alleen deze module importeren ook kunnen filteren. */
export { filterOptions }
